import { BalanceContext } from '@app/collections/fortress/BalanceContext'
import { Col, PageHeader, Row, Spacer, Tabs, Text, Title } from '@app/components'
import { IStore } from '@app/redux'
import { auth, Axios } from '@app/services'
import { persistTabValue } from '@app/utils'
import { useWalletChainId } from 'libs/ethereum/useWalletChainId'
import { useRouter } from 'next/router'
import { Fragment, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import SelectStateForm from '../buy-and-sell/SelectStateForm'
import { UserInfoContext } from '../fortress/UserInfoContext'
import { PiteasBuy } from '../piteas-buy'
import { PiteasSell } from '../piteas-sell'

const TabBarOnly = styled.div`
  .ant-tabs-content-holder {
    display: none;
  }
`

const HeaderLayout = styled(PageHeader)`
  .ant-page-header-heading {
    display: none;
  }
`

export const Piteas = () => {
  const router = useRouter()
  const [activeTab, setActiveTab] = useState('buy')
  const { usa, mtl, ogp, fetchUserInfo } = useContext(UserInfoContext)
  const { fetchAccounts } = useContext(BalanceContext)

  // somewhere above JSX in your component
  const isBuyOn = process.env.NEXT_PUBLIC_BUY_ON === 'true'
  const isBuyEligible = ogp || !usa // ogp users or international users
  const isUsNoOgp = usa && !ogp // US users without Old Glory Pay

  useEffect(() => {
    fetchUserInfo()
    fetchAccounts()
    const tab = router.query.tab as string
    if (tab?.toLowerCase() === 'sell') {
      setActiveTab('sell')
    }
  }, [])

  const { user } = useSelector((state: IStore) => state)
  const chainId = useWalletChainId()

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    persistTabValue(value)
  }

  const buySell = (
    <>
      <TabBarOnly>
        <Tabs
          activeKey={activeTab}
          items={[
            { key: 'buy', label: 'Buy' },
            { key: 'sell', label: 'Sell' },
          ]}
          onChange={handleTabChange}
        />
      </TabBarOnly>
      {activeTab === 'buy' ? <PiteasBuy key={`buy-${chainId}`} /> : <PiteasSell key={`sell-${chainId}`} />}
    </>
  )

  const updateUserState = async (chosenState: string) => {
    // call backend to update user state
    // For simplicity, assuming we have an API function updateUserStateAPI
    try {
      const authorization = await auth.currentUser.getIdToken()

      const headers = {
        Authorization: authorization,
      }

      const data = {
        uid: user.uid,
        usState: chosenState,
      }

      await Axios.post('/api/coast/state', data, { headers })
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error('Error updating user state:', error)
    }
  }

  return (
    <Row>
      <Col span={24}>
        <HeaderLayout
          disableBreadcrumb
          footer={
            <Fragment>
              <Title style={{ marginTop: 0, marginBottom: 8 }}>Buy And Sell</Title>
              <Text>
                On Ethereum: buy or sell HEX (eHEX). On PulseChain: PLS, PLSX, HEX, WAIT, INC, PHLPv2, or PRVX.
              </Text>
              <Spacer />

              {!isBuyOn ? (
                // Maintenance mode if BUY_ON is not true
                <>
                  <h3>Maintenance Mode</h3>
                  <Spacer />
                  <Text>
                    Buys &amp; Sells are temporarily disabled because the PulseChain network is experiencing high
                    congestion which is causing many buy orders to fail. This feature will be re-enabled once the
                    network congestion has subsided or the team has implemented a fix.
                  </Text>
                </>
              ) : (
                <>
                  {/* Normal flow: OGP users or international users */}
                  {isBuyEligible && buySell}

                  {/* US user without Old Glory Pay */}
                  {isUsNoOgp && (
                    <>
                      {mtl === 0 && buySell}

                      {mtl === 1 && (
                        <>
                          <Text>
                            Based on your state, the Buy &amp; Sell feature is only available if you use Old Glory Pay
                            for deposits.
                            <br />
                            If you think this is an error, please contact support through Intercom so our team can
                            review your account.
                          </Text>
                        </>
                      )}

                      {mtl === 2 && (
                        <>
                          <SelectStateForm
                            onSubmit={(chosenState) => {
                              // call backend to update user state
                              updateUserState(chosenState).then(() => fetchUserInfo())
                            }}
                          />
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Fragment>
          }
        />
      </Col>
    </Row>
  )
}
